// src/components/GuideModal.jsx
import React, { useState, useRef, useEffect } from "react";
import "./css/GuideModal.css";
import { db, auth } from "../firebase";
import {
    doc, setDoc, serverTimestamp, writeBatch, collection,
    getDocs,
    updateDoc
} from "firebase/firestore";
import { normalizeSlug } from "../utils/slug";

export const GUIDE_JSON = {
    title: "Bangkok",
    slug: "bangkok",

    country: {
        type: "country",
        name: "Thailand",
        code: "TH",
        continent: "Asia",
        currency: "THB",
        language: "Thai",
        flag: "🇹🇭"
    },

    city: {
        type: "city",
        name: "Bangkok",
        state: "Bangkok Metropolitan Region",
        population: "11M",
        timezone: "ICT (UTC+7)"
    },

    guide: {
        id: "bangkok",
        title: "Bangkok City Guide",
        subtitle: "Golden temples, street food, rooftop bars & vibrant markets.",
        pill: "City Travel Guide",
        heroClass: "bangkok",
        heroImage: ""
    },

    quickFacts: {
        region: "Central Thailand",
        bestMonths: "Nov–Feb",
        idealDuration: "3–5 days",
        dailyBudgetRange: "฿1,200–4,000",
        walkability: "Moderate (best with BTS & MRT)"
    },

    sections: {
        whyVisit: {
            title: "Why visit Bangkok?",
            description:
                "Bangkok blends ancient temples, buzzing street life, luxury malls, river cruises, and legendary street food. It is Thailand’s cultural and economic heart and a gateway to Southeast Asia."
        },

        bestTimeToVisit: {
            title: "Best time to visit",
            seasons: [
                { name: "Cool Season", emoji: "🌤️", months: "Nov–Feb", note: "Best weather & peak season" },
                { name: "Hot Season", emoji: "🔥", months: "Mar–May", note: "Very hot but fewer crowds" },
                { name: "Rainy Season", emoji: "🌧️", months: "Jun–Oct", note: "Short heavy showers" }
            ]
        },

        neighborhoods: {
            title: "Popular neighborhoods",
            areas: [
                { name: "Sukhumvit", emoji: "🌆", highlights: "Nightlife & shopping" },
                { name: "Old City (Rattanakosin)", emoji: "🏯", highlights: "Grand Palace & temples" },
                { name: "Silom", emoji: "🏙️", highlights: "Business district & rooftop bars" },
                { name: "Chinatown (Yaowarat)", emoji: "🥢", highlights: "Street food heaven" }
            ]
        },

        budget: {
            title: "Estimated daily budget",
            tiers: [
                { type: "Backpacker", range: "฿1,200–1,800" },
                { type: "Mid-range", range: "฿2,000–4,000", highlight: true },
                { type: "Luxury", range: "฿6,000+" }
            ]
        },

        gettingAround: {
            title: "Getting around Bangkok",
            description:
                "Use BTS Skytrain and MRT metro to avoid traffic. Grab and taxis are affordable. River boats connect major Old City attractions."
        },

        cta: {
            title: "Exploring Bangkok?",
            subtitle: "Save this guide or start planning your Thailand adventure.",
            actions: ["Add Trip", "Save Guide"]
        }
    },

    travelGuide: {
        wifiAndInternet: {
            title: "Wi-Fi & Internet",
            tips: [
                "Wi-Fi available in most hotels and cafés",
                "Free Wi-Fi in major malls",
                "Strong 4G/5G coverage city-wide"
            ]
        },

        simAndEsim: {
            title: "SIM & eSIM",
            options: [
                "Tourist SIMs available at airport",
                "Affordable unlimited data packages",
                "Top providers: AIS, TrueMove, DTAC"
            ],
            tip: "SIMs are cheap and easy to activate"
        },

        moneyAndPayments: {
            title: "Money & Payments",
            currency: "Thai Baht (฿)",
            tips: [
                "Cash used in street markets",
                "Cards accepted in malls & hotels",
                "ATMs widely available"
            ]
        },

        transportAndCabs: {
            title: "Transport & Cabs",
            tips: [
                "Use Grab to avoid fare negotiation",
                "Avoid rush-hour traffic",
                "River boats are scenic & efficient"
            ]
        },

        essentialApps: {
            title: "Best Apps to Install",
            categories: [
                { category: "Transport", apps: ["Grab", "Google Maps", "BTS Skytrain App"] },
                { category: "Food", apps: ["GrabFood", "Wongnai"] },
                { category: "Travel", apps: ["Google Translate", "Tripadvisor"] },
                { category: "Payments", apps: ["Wise", "Revolut"] }
            ]
        },

        safetyAndTips: {
            title: "Safety & Local Tips",
            tips: [
                "Beware of tuk-tuk scams near temples",
                "Dress modestly when visiting temples",
                "Stay hydrated due to heat",
                "Use official taxis or Grab"
            ]
        }
    },

    gallery: [],

    placesToVisit: [
        { label: "Grand Palace", placeId: "grand-palace-bangkok" },
        { label: "Wat Arun", placeId: "wat-arun" },
        { label: "Wat Pho", placeId: "wat-pho" },
        { label: "Chatuchak Market", placeId: "chatuchak-market" },
        { label: "Asiatique The Riverfront", placeId: "asiatique-riverfront" },
        { label: "Jim Thompson House", placeId: "jim-thompson-house" },
        { label: "Chinatown Bangkok", placeId: "chinatown-bangkok" },
        { label: "Lumphini Park", placeId: "lumphini-park" }
    ]
};

const PLACES_FOR_UPLOAD = [
    {
        id: "grand-palace-bangkok",
        slug: "grand-palace-bangkok",
        name: "Grand Palace",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "historic_site",
        shortDescription: "Former royal residence and home to the Emerald Buddha.",
        recommendedTime: "2–3 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 13.7500, lng: 100.4913 },
        images: [],
        tips: ["Dress modestly", "Arrive early", "Combine with Wat Pho"]
    },

    {
        id: "wat-arun",
        slug: "wat-arun",
        name: "Wat Arun",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "temple",
        shortDescription: "Iconic riverside temple known as the Temple of Dawn.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Sunset",
        entryFee: "Paid",
        location: { lat: 13.7437, lng: 100.4889 },
        images: [],
        tips: ["Best at sunset", "Take ferry across river", "Climb central tower"]
    },

    {
        id: "wat-pho",
        slug: "wat-pho",
        name: "Wat Pho",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "temple",
        shortDescription: "Temple of the Reclining Buddha.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 13.7465, lng: 100.4930 },
        images: [],
        tips: ["Try Thai massage school", "Visit early", "Respect dress code"]
    },

    {
        id: "chatuchak-market",
        slug: "chatuchak-market",
        name: "Chatuchak Market",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "market",
        shortDescription: "One of the world's largest weekend markets.",
        recommendedTime: "2–4 hours",
        bestTimeToVisit: "Weekend morning",
        entryFee: "Free",
        location: { lat: 13.7998, lng: 100.5500 },
        images: [],
        tips: ["Open weekends", "Carry cash", "Stay hydrated"]
    },

    {
        id: "asiatique-riverfront",
        slug: "asiatique-riverfront",
        name: "Asiatique The Riverfront",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "shopping_area",
        shortDescription: "Riverside night market & entertainment complex.",
        recommendedTime: "2 hours",
        bestTimeToVisit: "Evening",
        entryFee: "Free",
        location: { lat: 13.7056, lng: 100.5020 },
        images: [],
        tips: ["Free shuttle boat", "Great night views", "Good restaurants"]
    },

    {
        id: "jim-thompson-house",
        slug: "jim-thompson-house",
        name: "Jim Thompson House",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "museum",
        shortDescription: "Traditional Thai house museum showcasing silk heritage.",
        recommendedTime: "1–2 hours",
        bestTimeToVisit: "Morning",
        entryFee: "Paid",
        location: { lat: 13.7493, lng: 100.5280 },
        images: [],
        tips: ["Join guided tour", "Peaceful atmosphere", "Near Siam"]
    },

    {
        id: "chinatown-bangkok",
        slug: "chinatown-bangkok",
        name: "Chinatown Bangkok",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "neighborhood",
        shortDescription: "Yaowarat district known for street food.",
        recommendedTime: "2–3 hours",
        bestTimeToVisit: "Evening",
        entryFee: "Free",
        location: { lat: 13.7396, lng: 100.5099 },
        images: [],
        tips: ["Best at night", "Try seafood", "Very busy weekends"]
    },

    {
        id: "lumphini-park",
        slug: "lumphini-park",
        name: "Lumphini Park",
        city: "bangkok",
        country: "thailand",
        guidesId: "bangkok",
        type: "park",
        shortDescription: "Green park in central Bangkok.",
        recommendedTime: "1 hour",
        bestTimeToVisit: "Morning or evening",
        entryFee: "Free",
        location: { lat: 13.7300, lng: 100.5418 },
        images: [],
        tips: ["Spot monitor lizards", "Great for jogging", "Relaxing escape"]
    }
];

export default function GuideModal({ open, onClose, currentUserId }) {
    const titleRef = useRef(null);
    const [title, setTitle] = useState("");
    const [slug, setSlug] = useState("");
    const [country, setCountry] = useState("");
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [guideJsonInput, setGuideJsonInput] = useState("");
    const [placesJsonInput, setPlacesJsonInput] = useState("");


    useEffect(() => {
        if (open) {
            setTimeout(() => titleRef.current?.focus(), 50);
        }
    }, [open]);

    if (!open) return null;

    const resetForm = () => {
        setTitle("");
        setSlug("");
        setCountry("");
        setGuideJsonInput("");
        setPlacesJsonInput("");
        setError(null);
        setSaving(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSave = async () => {
        if (!guideJsonInput.trim() && !placesJsonInput.trim()) {
            setError("Nothing to save");
            return;
        }

        setSaving(true);
        setError(null);

        try {
            if (guideJsonInput.trim()) {
                await uploadGuide();
            }

            if (placesJsonInput.trim()) {
                await uploadPlaces();
            }

            alert("Saved successfully ✅");
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };
    const uploadGuide = async () => {
        try {
            if (!auth.currentUser) {
                throw new Error("You must be logged in");
            }

            if (!guideJsonInput.trim()) {
                throw new Error("Guide JSON is empty");
            }

            setSaving(true);
            setError(null);

            const parsedJSON = JSON.parse(guideJsonInput);
            const normalizedSlug = normalizeSlug(
                slug || parsedJSON.slug || title
            );

            await setDoc(
                doc(db, "guides", normalizedSlug),
                {
                    ...parsedJSON,
                    id: normalizedSlug,
                    slug: normalizedSlug,
                    createdBy: auth.currentUser.uid,

                    // 🔥 Social Metrics (Initialize once)
                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false } // important for clean creation
            );
            await setDoc(
                doc(db, "slugs", normalizedSlug),
                {
                    guideId: normalizedSlug,
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to upload guide");
            throw err; // important so handleSave knows it failed
        } finally {
            setSaving(false);
        }
    };

    const uploadPlaces = async () => {
        if (!auth.currentUser) {
            throw new Error("You must be logged in");
        }

        if (!placesJsonInput.trim()) {
            throw new Error("Places JSON is empty");
        }

        const places = JSON.parse(placesJsonInput);
        if (!Array.isArray(places)) {
            throw new Error("Places JSON must be an array");
        }

        const batch = writeBatch(db);

        places.forEach((place) => {
            if (!place.id || !place.name) {
                throw new Error("Each place must have id and name");
            }

            const placeSlug = normalizeSlug(place.slug || place.name);

            // 1️⃣ Save place document
            batch.set(
                doc(db, "places", place.id),
                {
                    ...place,
                    slug: placeSlug,
                    createdBy: auth.currentUser.uid,

                    likesCount: 0,
                    views: 0,
                    savesCount: 0,

                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                },
                { merge: false }
            );

            // 2️⃣ Register slug
            batch.set(
                doc(db, "slugs", placeSlug),
                {
                    placeId: place.id, // ✅ new supported type
                    ownerId: auth.currentUser.uid,
                    createdAt: serverTimestamp(),
                },
                { merge: false }
            );
        });

        await batch.commit();
    };

    const loadGuideJSON = (json) => {
        try {
            setTitle(json.title || "");
            setSlug(json.slug || json.id || "");
            setCountry(json.country?.name || "");
            setGuideJsonInput(JSON.stringify(json, null, 2));
            setError(null);
        } catch {
            setError("Invalid Guide JSON");
        }
    };
    const loadPlacesJSON = (placesArray) => {
        if (!Array.isArray(placesArray)) {
            setError("Places JSON must be an array");
            return;
        }
        setPlacesJsonInput(JSON.stringify(placesArray, null, 2));
    };
    const fixAllPlacesCounters = async () => {
        if (!auth.currentUser) {
            alert("You must be logged in");
            return;
        }

        if (!window.confirm("This will update ALL places. Continue?")) {
            return;
        }

        try {
            setSaving(true);

            const snap = await getDocs(collection(db, "places"));

            let updatedCount = 0;

            for (const docSnap of snap.docs) {
                const data = docSnap.data();

                const needsUpdate =
                    typeof data.likesCount !== "number" ||
                    typeof data.savesCount !== "number" ||
                    typeof data.views !== "number";

                if (needsUpdate) {
                    await updateDoc(docSnap.ref, {
                        likesCount:
                            typeof data.likesCount === "number"
                                ? data.likesCount
                                : 0,
                        savesCount:
                            typeof data.savesCount === "number"
                                ? data.savesCount
                                : 0,
                        views:
                            typeof data.views === "number"
                                ? data.views
                                : 0,
                    });

                    updatedCount++;
                }
            }

            alert(`✅ Updated ${updatedCount} places`);
        } catch (err) {
            console.error(err);
            alert("Error updating places");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal-backdrop"
            onMouseDown={(e) => e.target === e.currentTarget && handleClose()}
        >
            <div className="modal guide-modal">
                <h3>Create Guide</h3>

                {/* Title */}
                <label>Title *</label>
                <input
                    ref={titleRef}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="France Travel Guide"
                />

                {/* Slug */}
                <label>Slug (optional)</label>
                <input
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder="france"
                />

                {/* Country */}
                <label>Country</label>
                <input
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder="France"
                />

                {/* JSON */}
                <label>Guide JSON *</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={guideJsonInput}
                    onChange={(e) => setGuideJsonInput(e.target.value)}
                    placeholder="{ ...full guide json... }"
                />

                <label>Places JSON</label>
                <textarea
                    rows={14}
                    className="json-preview"
                    value={placesJsonInput}
                    onChange={(e) => setPlacesJsonInput(e.target.value)}
                    placeholder="[ { ...place }, { ...place } ]"
                />

                {error && <div className="error">{error}</div>}

                <div className="controls">
                    <button className="btn-secondary" onClick={handleClose}>
                        Cancel
                    </button>
                    <div className="json-actions">
                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadGuideJSON(GUIDE_JSON)}
                        >
                            📘 Load Guide JSON
                        </button>

                        <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => loadPlacesJSON(PLACES_FOR_UPLOAD)}
                        >
                            📍 Load Places JSON
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={fixAllPlacesCounters}
                        disabled={saving}
                    >
                        🔧 Fix All Places Counters
                    </button>

                    <button
                        className="btn-primary"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Saving..." : "Save Guide"}
                    </button>


                </div>
            </div>
        </div>
    );
}
